import type { Island } from '../island.class';
import type { Size } from '../../../types';
import { computeAutoResizeWidthCfn } from './auto-resize-width.cfn';

export interface CleanIslandOptions {
	/** Size the island to its contents (skipped on a size-locked island) */
	autoResize?: boolean;
	/** With `autoResize`, grow when the contents overflow but never shrink */
	growOnly?: boolean;
}

/**
 * Size one island to its contents. Returns the new width, or null when the
 * width was left as it was (a height/depth clamp alone is not reported — that
 * is a minimum being enforced, not a resize anyone asked about).
 *
 * Skips the assignment when nothing moves: `@Publish` on the size setter
 * compares old and new by identity and the setter always stores a fresh object,
 * so re-writing the same numbers would still emit `island_size.updated` and cost
 * the client a scene rebuild.
 */
export const autoResizeIslandCfn = (island: Island, growOnly = false): number | null => {
	const frontPanel = island.panels.getFrontPanel();
	if (!frontPanel) return null;

	const { minimumSize } = island;
	const currentSize = island.size as Size;
	const { width: currentWidth, height: currentHeight } = currentSize;
	const currentDepth = currentSize.depth as number;

	const { finalWidth } = computeAutoResizeWidthCfn({
		groupWidths: frontPanel.panelItemGroups.children.map((g) => g.maxOutsideWidth),
		overlapTotal: frontPanel.overlapWidth,
		currentWidth,
		minimumWidth: frontPanel.minimumWidth,
		minimumSizeWidth: minimumSize?.width ?? null,
		growOnly
	});
	const finalHeight = minimumSize ? Math.max(currentHeight, minimumSize.height) : currentHeight;
	const finalDepth = minimumSize ? Math.max(currentDepth, minimumSize.depth as number) : currentDepth;

	if (finalWidth === currentWidth && finalHeight === currentHeight && finalDepth === currentDepth) return null;

	island.size = { width: finalWidth, height: finalHeight, depth: finalDepth };

	return finalWidth === currentWidth ? null : finalWidth;
};

/**
 * `clean_kitchen` for ONE island: purge empty columns, optionally size the
 * island to its contents, then re-space every panel horizontally and re-centre
 * every column vertically. Returns the new width when the island was resized.
 */
export const cleanIslandCfn = (island: Island, options: CleanIslandOptions = {}): number | null => {
	const { autoResize = false, growOnly = false } = options;

	island.panels.children.forEach((panel) => {
		panel.panelItemGroups.purgeEmpty();
	});

	let resized: number | null = null;
	if (autoResize && !island.sizeLocked) {
		resized = autoResizeIslandCfn(island, growOnly);
	}

	island.panels.children.forEach((panel) => {
		// a new width lays the run out afresh; hand-placed positions would no longer fit it
		if (resized !== null) panel.resetFineTune();
		panel.distributeApplianceGroupsHorizontally();
		panel.panelItemGroups.children.forEach((group) => {
			group.centerItemsVertically();
		});
	});

	return resized;
};
